import { Select, DatePicker, Table, Tag, Button } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import usePublications from "hooks/usePublications";
import DonutChart from "components/charts/DonutChart";
import Chart from "react-apexcharts";
import Card from "components/card";
import Loader from "components/loader";
import { FaFilePdf } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import { DASHBOARD_COLORS } from "utils/dashboardColors";
import { routesNames } from "utils/Constants";
import PickApplyLogo from "assets/img/admin.png";
import {
    FETCH_LIMIT,
    RANGE_OPTIONS,
    getRangeBounds,
    fetchInstitucionesMap,
    resolveInstitucion,
    institucionKey,
} from "utils/postulacionesHelpers";

const { RangePicker } = DatePicker;

const DirectasEspontaneasReport = () => {
    const { isLoading, response, getPostulateds } = usePublications();
    const [rangeType, setRangeType] = useState("mes");
    const [customRange, setCustomRange] = useState(null);
    const [institucionesMap, setInstitucionesMap] = useState(new Map());
    const [institucionSeleccionada, setInstitucionSeleccionada] = useState(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const pdfHeaderRef = useRef(null);
    const pdfTableRef = useRef(null);

    useEffect(() => {
        getPostulateds(null, FETCH_LIMIT);
        fetchInstitucionesMap().then(setInstitucionesMap);
    }, []);

    const dateFilteredData = useMemo(() => {
        const [from, to] = getRangeBounds(rangeType, customRange);
        if (!from || !to) return [];

        return (response || []).filter((item) => {
            const created = dayjs(item.created_at);
            return !created.isBefore(from) && !created.isAfter(to);
        });
    }, [response, rangeType, customRange]);

    const institucionOptions = useMemo(() => {
        const map = new Map();

        dateFilteredData.forEach((record) => {
            const cuenta = resolveInstitucion(record, institucionesMap);
            const key = institucionKey(record, institucionesMap);
            if (key && cuenta?.nombre && !map.has(key)) {
                map.set(key, { label: cuenta.nombre, value: key });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [dateFilteredData, institucionesMap]);

    // Si la institución elegida deja de aparecer en las opciones (cambió el rango
    // de fecha), volvemos a "Todas" en vez de dejar un filtro fantasma.
    useEffect(() => {
        if (institucionSeleccionada && !institucionOptions.some((opt) => opt.value === institucionSeleccionada)) {
            setInstitucionSeleccionada(null);
        }
    }, [institucionOptions, institucionSeleccionada]);

    const institucionSeleccionadaNombre = useMemo(
        () => institucionOptions.find((opt) => opt.value === institucionSeleccionada)?.label || "",
        [institucionOptions, institucionSeleccionada]
    );

    const rangeLabel = useMemo(() => {
        if (rangeType === "personalizado") {
            if (!customRange?.[0] || !customRange?.[1]) return "Rango personalizado";
            return `${customRange[0].format("DD/MM/YYYY")} — ${customRange[1].format("DD/MM/YYYY")}`;
        }
        return RANGE_OPTIONS.find((opt) => opt.value === rangeType)?.label || "";
    }, [rangeType, customRange]);

    const scopedData = useMemo(() => {
        if (!institucionSeleccionada) return dateFilteredData;
        return dateFilteredData.filter((record) => institucionKey(record, institucionesMap) === institucionSeleccionada);
    }, [dateFilteredData, institucionSeleccionada, institucionesMap]);

    const stats = useMemo(() => {
        const total = scopedData.length;
        const directas = scopedData.filter((r) => r.publicacion_id != null).length;
        const espontaneas = total - directas;

        return {
            total,
            directas,
            espontaneas,
            pctDirectas: total > 0 ? Math.round((directas / total) * 1000) / 10 : 0,
            pctEspontaneas: total > 0 ? Math.round((espontaneas / total) * 1000) / 10 : 0,
        };
    }, [scopedData]);

    const trendData = useMemo(() => {
        const byDay = new Map();

        scopedData.forEach((record) => {
            const day = dayjs(record.created_at).format("YYYY-MM-DD");
            if (!byDay.has(day)) byDay.set(day, { directas: 0, espontaneas: 0 });
            const entry = byDay.get(day);
            if (record.publicacion_id != null) entry.directas += 1;
            else entry.espontaneas += 1;
        });

        return Array.from(byDay.entries())
            .sort(([a], [b]) => (a > b ? 1 : -1))
            .map(([day, counts]) => ({ day, ...counts }));
    }, [scopedData]);

    const donutOptions = useMemo(
        () => ({
            chart: { type: "donut" },
            labels: ["Espontáneas", "Directas"],
            colors: [DASHBOARD_COLORS.varios, DASHBOARD_COLORS.otros],
            legend: { show: true, position: "bottom" },
            dataLabels: { enabled: true },
            tooltip: { theme: "dark" },
        }),
        []
    );

    const trendChartOptions = useMemo(
        () => ({
            chart: { toolbar: { show: false } },
            xaxis: {
                categories: trendData.map((d) => dayjs(d.day).format("DD/MM")),
                labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
            },
            colors: [DASHBOARD_COLORS.varios, DASHBOARD_COLORS.otros],
            legend: { show: true, position: "top" },
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: 2 },
            grid: { show: false },
            tooltip: { theme: "dark" },
        }),
        [trendData]
    );

    const trendSeries = [
        { name: "Espontáneas", data: trendData.map((d) => d.espontaneas) },
        { name: "Directas", data: trendData.map((d) => d.directas) },
    ];

    // "Todas las instituciones": ranking de las 10 instituciones con más
    // postulaciones recibidas en el rango, con el desglose directas/espontáneas.
    const topInstituciones = useMemo(() => {
        const map = new Map();

        dateFilteredData.forEach((record) => {
            const cuenta = resolveInstitucion(record, institucionesMap);
            const key = institucionKey(record, institucionesMap);
            if (!key || !cuenta?.nombre) return;

            if (!map.has(key)) {
                map.set(key, { key, nombre: cuenta.nombre, user_id: cuenta.user_id, total: 0, directas: 0, espontaneas: 0 });
            }

            const entry = map.get(key);
            entry.total += 1;
            if (record.publicacion_id != null) entry.directas += 1;
            else entry.espontaneas += 1;
        });

        return Array.from(map.values())
            .sort((a, b) => b.total - a.total)
            .slice(0, 10);
    }, [dateFilteredData, institucionesMap]);

    // Institución seleccionada: ranking de sus publicaciones con más postulaciones,
    // sumando también "Espontáneas" como una fila más si tiene.
    const topPublicaciones = useMemo(() => {
        if (!institucionSeleccionada) return [];

        const map = new Map();
        let espontaneasCount = 0;

        scopedData.forEach((record) => {
            if (record.publicacion_id != null && record.publicacion?.nombre) {
                const key = record.publicacion_id;
                if (!map.has(key)) {
                    map.set(key, { key, publicacion_id: key, nombre: record.publicacion.nombre, total: 0 });
                }
                map.get(key).total += 1;
            } else if (record.publicacion_id == null) {
                espontaneasCount += 1;
            }
        });

        const rows = Array.from(map.values());
        if (espontaneasCount > 0) {
            rows.push({ key: "espontaneas", publicacion_id: null, nombre: "Espontáneas", total: espontaneasCount, isEspontanea: true });
        }

        return rows.sort((a, b) => b.total - a.total);
    }, [scopedData, institucionSeleccionada]);

    // Captura el reporte en dos partes (header+gráficos, y la tabla) y arma un PDF,
    // forzando salto de página entre ambas para que no se corten los gráficos ni la
    // tabla quede pegada a la mitad de un gráfico.
    const handleDownloadPdf = async () => {
        if (!pdfHeaderRef.current || !pdfTableRef.current) return;

        setIsGeneratingPdf(true);
        try {
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const marginMm = 6;
            const pageWidthMm = pdf.internal.pageSize.getWidth();
            const pageHeightMm = pdf.internal.pageSize.getHeight();
            const usableWidthMm = pageWidthMm - marginMm * 2;
            const usableHeightMm = pageHeightMm - marginMm * 2;

            const canvasOptions = { useCORS: true, allowTaint: true, scale: 2, backgroundColor: "#ffffff" };

            // Agrega una sección (ya capturada como canvas) partiéndola en tantas
            // páginas como haga falta si no entra completa en una sola.
            const addSection = (canvas, startNewPage) => {
                if (startNewPage) pdf.addPage();

                const imgData = canvas.toDataURL("image/png");
                const imgHeightMm = (canvas.height * usableWidthMm) / canvas.width;

                let heightLeftMm = imgHeightMm;
                let renderedHeightMm = 0;

                pdf.addImage(imgData, "PNG", marginMm, marginMm, usableWidthMm, imgHeightMm);
                heightLeftMm -= usableHeightMm;

                while (heightLeftMm > 0) {
                    renderedHeightMm += usableHeightMm;
                    pdf.addPage();
                    pdf.addImage(imgData, "PNG", marginMm, marginMm - renderedHeightMm, usableWidthMm, imgHeightMm);
                    heightLeftMm -= usableHeightMm;
                }
            };

            const headerCanvas = await html2canvas(pdfHeaderRef.current, canvasOptions);
            addSection(headerCanvas, false);

            const tableCanvas = await html2canvas(pdfTableRef.current, canvasOptions);
            addSection(tableCanvas, true);

            const scopeLabel = institucionSeleccionada ? institucionSeleccionadaNombre : "general";
            const fileName = `Reporte_postulaciones_${scopeLabel}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`
                .replace(/\s+/g, "_")
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "");

            pdf.save(fileName);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-3xl font-medium">/ Directas vs Espontáneas</h1>

                {stats.total > 0 && (
                    <Button
                        icon={<FaFilePdf />}
                        loading={isGeneratingPdf}
                        onClick={handleDownloadPdf}
                        className="flex items-center gap-2 !bg-red-500 !text-white hover:!bg-red-600"
                    >
                        Descargar PDF
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Select
                    value={rangeType}
                    onChange={(value) => setRangeType(value)}
                    options={RANGE_OPTIONS}
                    className="w-[180px]"
                />

                {rangeType === "personalizado" && (
                    <RangePicker
                        format="DD/MM/YYYY"
                        value={customRange}
                        onChange={(value) => setCustomRange(value)}
                    />
                )}

                <Select
                    showSearch
                    allowClear
                    placeholder="Todas las instituciones"
                    value={institucionSeleccionada}
                    onChange={(value) => setInstitucionSeleccionada(value || null)}
                    options={institucionOptions}
                    optionFilterProp="label"
                    className="w-[280px]"
                />
            </div>

            {isLoading ? (
                <Loader />
            ) : (
                <div className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 items-start">
                        <Card extra="!p-5 flex flex-col gap-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</span>
                            <span className="text-2xl font-bold text-navy-700 dark:text-white">{stats.total}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">postulaciones en el rango</span>
                        </Card>
                        <Card extra="!p-5 flex flex-col gap-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Directas</span>
                            <span className="text-2xl font-bold text-navy-700 dark:text-white">{stats.directas}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{stats.pctDirectas}% del total</span>
                        </Card>
                        <Card extra="!p-5 flex flex-col gap-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Espontáneas</span>
                            <span className="text-2xl font-bold text-navy-700 dark:text-white">{stats.espontaneas}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{stats.pctEspontaneas}% del total</span>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 items-start">
                        <Card extra="w-full h-[420px] px-6 py-4">
                            <div className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                                Distribución
                            </div>
                            {stats.total > 0 ? (
                                <div className="mt-2 flex h-[340px] w-full items-center justify-center">
                                    <DonutChart options={donutOptions} series={[stats.espontaneas, stats.directas]} />
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos en el rango seleccionado</p>
                            )}
                        </Card>

                        <Card extra="w-full h-[420px] px-6 py-4 lg:col-span-2">
                            <div className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                                Evolución en el rango
                            </div>
                            {trendData.length > 0 ? (
                                <div className="h-[340px] w-full">
                                    <Chart options={trendChartOptions} series={trendSeries} type="line" width="100%" height="100%" />
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos en el rango seleccionado</p>
                            )}
                        </Card>
                    </div>

                    <Card extra="w-full px-6 py-4">
                        {!institucionSeleccionada ? (
                            <>
                                <div className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                                    Top 10 instituciones con más postulaciones
                                </div>
                                <Table
                                    dataSource={topInstituciones}
                                    rowKey="key"
                                    size="small"
                                    pagination={false}
                                    locale={{ emptyText: "No hay datos en el rango seleccionado" }}
                                    columns={[
                                        {
                                            title: "Institución",
                                            dataIndex: "nombre",
                                            render: (text, record) => (
                                                record.user_id ? (
                                                    <Link to={`${routesNames.USER}/${record.user_id}`} className="text-sm font-semibold text-blue-800 dark:text-white">
                                                        {record.nombre}
                                                    </Link>
                                                ) : record.nombre
                                            ),
                                        },
                                        { title: "Total", dataIndex: "total", sorter: (a, b) => a.total - b.total, defaultSortOrder: "descend" },
                                        { title: "Directas", dataIndex: "directas" },
                                        { title: "Espontáneas", dataIndex: "espontaneas" },
                                        {
                                            title: "",
                                            dataIndex: "acciones",
                                            render: (text, record) => (
                                                <Button size="small" onClick={() => setInstitucionSeleccionada(record.key)}>
                                                    Seleccionar
                                                </Button>
                                            ),
                                        },
                                    ]}
                                />
                            </>
                        ) : (
                            <>
                                <div className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                                    Publicaciones con más postulaciones
                                </div>
                                <Table
                                    dataSource={topPublicaciones}
                                    rowKey="key"
                                    size="small"
                                    pagination={false}
                                    locale={{ emptyText: "No hay datos en el rango seleccionado" }}
                                    columns={[
                                        {
                                            title: "Publicación",
                                            dataIndex: "nombre",
                                            render: (text, record) => (
                                                record.isEspontanea ? (
                                                    <Tag>Espontáneas</Tag>
                                                ) : (
                                                    <Link to={`${routesNames.PUBLICATION}/${record.publicacion_id}`} className="text-sm font-semibold text-blue-800 dark:text-white">
                                                        {record.nombre}
                                                    </Link>
                                                )
                                            ),
                                        },
                                        { title: "Postulaciones", dataIndex: "total", sorter: (a, b) => a.total - b.total, defaultSortOrder: "descend" },
                                    ]}
                                />
                            </>
                        )}
                    </Card>
                </div>
            )}

            {/* Layout exclusivo para el PDF: más angosto (tipo A4), apilado en una sola
                columna y con tipografía más grande. Se renderiza siempre (fuera de
                pantalla) para que el gráfico ya esté dibujado cuando se genera el PDF. */}
            <div
                ref={pdfHeaderRef}
                aria-hidden="true"
                className="pointer-events-none fixed -left-[9999px] top-0 flex w-[820px] flex-col gap-5 bg-white p-5"
            >
                <div className="flex items-center justify-between border-b-4 border-main pb-3">
                    <img src={PickApplyLogo} alt="PickApply" className="h-16" />
                    <span className="text-sm font-semibold text-gray-500">Reporte de postulaciones</span>
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-[32px] font-extrabold text-navy-700">
                        {institucionSeleccionada ? institucionSeleccionadaNombre : "Reporte general de postulaciones"}
                    </span>
                    <span className="mt-1 text-base text-gray-500">
                        Rango: {rangeLabel} · Generado el {dayjs().format("DD/MM/YYYY HH:mm")}
                    </span>
                </div>

                <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-6">
                    <span className="text-2xl font-bold text-navy-700">Distribución</span>
                    {stats.total > 0 ? (
                        <div className="flex items-center gap-6">
                            <div className="flex h-[280px] w-[55%] items-center justify-center">
                                <DonutChart options={donutOptions} series={[stats.espontaneas, stats.directas]} />
                            </div>
                            <div className="flex w-[45%] flex-col gap-4">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">Total</span>
                                    <span className="text-3xl font-bold text-navy-700">{stats.total}</span>
                                    <span className="text-sm text-gray-500">postulaciones</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">Directas</span>
                                    <span className="text-3xl font-bold text-navy-700">{stats.directas}</span>
                                    <span className="text-sm text-gray-500">{stats.pctDirectas}% del total</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">Espontáneas</span>
                                    <span className="text-3xl font-bold text-navy-700">{stats.espontaneas}</span>
                                    <span className="text-sm text-gray-500">{stats.pctEspontaneas}% del total</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-base text-gray-500">No hay datos en el rango seleccionado</p>
                    )}
                </div>

                <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-6">
                    <span className="text-2xl font-bold text-navy-700">Evolución en el rango</span>
                    {trendData.length > 0 ? (
                        <div className="h-[300px] w-full">
                            <Chart options={trendChartOptions} series={trendSeries} type="line" width="100%" height="100%" />
                        </div>
                    ) : (
                        <p className="text-base text-gray-500">No hay datos en el rango seleccionado</p>
                    )}
                </div>
            </div>

            <div
                ref={pdfTableRef}
                aria-hidden="true"
                className="pointer-events-none fixed -left-[9999px] top-0 flex w-[820px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-6"
            >
                <span className="text-2xl font-bold text-navy-700">
                    {institucionSeleccionada ? "Publicaciones con más postulaciones" : "Top 10 instituciones con más postulaciones"}
                </span>

                {!institucionSeleccionada ? (
                    <table className="w-full border-collapse text-base">
                        <thead>
                            <tr className="border-b-2 border-gray-300 text-left">
                                <th className="py-2 pr-2 text-sm font-bold uppercase text-gray-500">Institución</th>
                                <th className="py-2 px-2 text-right text-sm font-bold uppercase text-gray-500">Total</th>
                                <th className="py-2 px-2 text-right text-sm font-bold uppercase text-gray-500">Directas</th>
                                <th className="py-2 pl-2 text-right text-sm font-bold uppercase text-gray-500">Espontáneas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topInstituciones.length === 0 && (
                                <tr><td colSpan={4} className="py-3 text-gray-500">No hay datos en el rango seleccionado</td></tr>
                            )}
                            {topInstituciones.map((row) => (
                                <tr key={row.key} className="border-b border-gray-100">
                                    <td className="py-2 pr-2 font-semibold text-navy-700">{row.nombre}</td>
                                    <td className="py-2 px-2 text-right">{row.total}</td>
                                    <td className="py-2 px-2 text-right">{row.directas}</td>
                                    <td className="py-2 pl-2 text-right">{row.espontaneas}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="w-full border-collapse text-base">
                        <thead>
                            <tr className="border-b-2 border-gray-300 text-left">
                                <th className="py-2 pr-2 text-sm font-bold uppercase text-gray-500">Publicación</th>
                                <th className="py-2 pl-2 text-right text-sm font-bold uppercase text-gray-500">Postulaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topPublicaciones.length === 0 && (
                                <tr><td colSpan={2} className="py-3 text-gray-500">No hay datos en el rango seleccionado</td></tr>
                            )}
                            {topPublicaciones.map((row) => (
                                <tr key={row.key} className="border-b border-gray-100">
                                    <td className="py-2 pr-2 font-semibold text-navy-700">{row.nombre}</td>
                                    <td className="py-2 pl-2 text-right">{row.total}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DirectasEspontaneasReport;
